import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, map, of, take, throwError } from 'rxjs';

import {
  DeleteGlobalEntitiesByFilterRequest,
  DeleteGlobalEntitiesRequest,
  DeletePlanEntitiesByFilterRequest,
  DeletePlanEntitiesRequest
} from '@gfe/core/models/entity/delete-entity-request.model';
import { CreateOrUpdateGlobalEntityRequest } from '@gfe/core/models/entity/entity-selector-request.model';
import { GbeErrorCodes } from '@gfe/core/models/gbe/gbe-error-codes.enum';
import { Transformer } from '@gfe/core/models/transformers.model';
import { EntityModificationService } from '@gfe/core/services/entity-modification/entity-modification.service';
import { occurrenceText } from '@gfe/core/services/gbe-error-parser/entity-ref-labels';
import { GbeErrorParserService } from '@gfe/core/services/gbe-error-parser/gbe-error-parser.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { PlansService } from '@gfe/core/services/plans/plans.service';
import { SelectorsService } from '@gfe/core/services/selectors/selectors.service';
import { isNil } from '@gfe/core/utils/shared-utils';
import { FilterConfiguration } from '@gfe/shared/components/filter-query-builder/filter.models';
import { FormBuilderRowResult } from '@gfe/shared/components/row-edition-form/row-edition-form.component';
import { FormMode } from '@gfe/shared/components/row-edition-form/row-edition-form.utils';

import { FilterExpr } from '@core/filters/path-filter/wire';

interface DeleteByUid {
  entityType: string;
  uids: string[];
  dataSpaceId?: number;
  planId: string | null | undefined;
}

interface DeleteByFilter {
  entityType: string;
  filters: {
    filters?: FilterConfiguration;
    search?: string;
    pathFilter?: FilterExpr;
  };
  dataSpaceId?: number;
  planId: string | null | undefined;
}

interface SaveRow {
  entityType: string;
  entityTypeVersion: string;
  rowId: string;
  values: FormBuilderRowResult;
  dataSpaceId?: number;
  formMode: FormMode;
  transformer: Transformer;
}

@Injectable()
export class EntityRegistryService {
  private readonly selectorsService = inject(SelectorsService);
  private readonly plansService = inject(PlansService);
  private readonly entityModificationService = inject(EntityModificationService);
  private readonly notificationService = inject(NotificationService);
  private readonly gbeErrorParserService = inject(GbeErrorParserService);
  private readonly translate = inject(TranslateService);

  saveRow(data: SaveRow): Observable<void> {
    const values = data.values;
    if (data.formMode === 'edit') {
      values['uid'] = data.rowId;
    }

    const updateRequest: CreateOrUpdateGlobalEntityRequest = {
      data: values,
      transformer: {
        name: data.transformer.name,
        version: data.transformer.version
      },
      dataSpaceId: data.dataSpaceId,
      entityTypeVersion: data.entityTypeVersion
    };

    const createOrUpdate: Observable<void | { uid?: string }> =
      data.formMode === 'create'
        ? this.entityModificationService.planAwareCreate$(data.entityType, updateRequest)
        : this.entityModificationService.planAwareUpdate$(data.entityType, updateRequest);

    return createOrUpdate.pipe(
      take(1),
      map(() => {}),
      catchError((err) => {
        console.error('Error updating entity:', err);
        return throwError(() => err);
      })
    );
  }

  deleteByUids(data: DeleteByUid): Observable<void> {
    if ((data.uids ?? []).length === 0) {
      return of();
    }

    if (isNil(data.dataSpaceId)) {
      return throwError(() => new Error('dataSpaceId is required'));
    }

    const { entityType, uids, dataSpaceId, planId } = data;

    const deleteEntitiesRequest = {
      uids,
      entityType: entityType.toUpperCase(),
      ...(isNil(planId) ? { dataSpaceId } : {})
    };

    const deleteObservable$ = isNil(planId)
      ? this.selectorsService.deleteEntities$(
          deleteEntitiesRequest as DeleteGlobalEntitiesRequest
        )
      : this.plansService.deleteEntities$(
          planId,
          deleteEntitiesRequest as DeletePlanEntitiesRequest
        );
    return deleteObservable$.pipe(
      take(1),
      catchError((err) => {
        console.error('Error deleting entities:', deleteEntitiesRequest, err);
        return throwError(() => err);
      })
    );
  }

  deleteByFilter(data: DeleteByFilter): Observable<void> {
    if (isNil(data.dataSpaceId) || isNil(data.filters)) {
      console.error('dataSpaceId and filters are required');
      return throwError(() => new Error('dataSpaceId and filters are required'));
    }

    const { entityType, dataSpaceId, planId, filters } = data;

    let deleteObservable$: Observable<void>;
    if (isNil(planId)) {
      const dataRequest: DeleteGlobalEntitiesByFilterRequest = {
        dataSpaceId,
        entityType: entityType.toUpperCase(),
        filters: filters.filters,
        search: filters.search,
        ...(isNil(filters.pathFilter) ? {} : { pathFilter: filters.pathFilter })
      };
      deleteObservable$ = this.selectorsService.deleteEntitiesByFilter$(dataRequest);
    } else {
      const dataRequest: DeletePlanEntitiesByFilterRequest = {
        entityType: entityType.toUpperCase(),
        filters: filters.filters,
        search: filters.search,
        ...(isNil(filters.pathFilter) ? {} : { pathFilter: filters.pathFilter })
      };
      deleteObservable$ = this.plansService.deleteEntitiesByFilter$(planId, dataRequest);
    }

    return deleteObservable$.pipe(
      take(1),
      catchError((error) => {
        if (
          [GbeErrorCodes.MIN_ITEMS_ERROR, GbeErrorCodes.MAX_ITEMS_ERROR].includes(
            error.errorCode
          )
        ) {
          const views = this.gbeErrorParserService.resolveError(error);
          this.notificationService.warning(
            views[0]?.title ?? error.errorCode ?? '',
            views
              .flatMap((view) => view.occurrences)
              .map((occurrence) => occurrenceText(occurrence))
              .join(' '),
            5000
          );
        }
        return of();
      })
    );
  }
}
